import { isbot } from 'isbot';

export type BotType = 'verified' | 'crawler' | 'automated';

export type BotSource = 'cloudflare' | 'user_agent' | 'header' | 'webdriver';

export interface Bot {
    isBot: boolean;
    type: BotType | null;
    name: string | null;
    category: string | null;
    sources: BotSource[];
}

// From the strongest signal to the weakest
const types: BotType[] = ['verified', 'crawler', 'automated'];

export const noBot: Bot = {
    isBot: false,
    type: null,
    name: null,
    category: null,
    sources: [],
};

// The `x-is-bot` header read by the SSR server
export const headerBot: Bot = {
    isBot: true,
    type: 'crawler',
    name: null,
    category: null,
    sources: ['header'],
};

// The product token of the bot (GPTBot, Googlebot, HeadlessChrome...), isbot only returns the
// part of the user agent its patterns matched
export function getBotName(userAgent: string): string | null {
    const [, name = null] =
        userAgent.match(/([\w.-]*(?:bot|crawl|spider|slurp|fetch|externalhit|headless)[\w.-]*)/i) ||
        [];
    if (name !== null) {
        return name;
    }
    const [product = null] = userAgent.split(/[\s/;(]/);
    return product !== null && product !== '' && product !== 'Mozilla' ? product : null;
}

export function detectBrowserBot(): Bot {
    if (typeof navigator === 'undefined') {
        return noBot;
    }
    const { userAgent = '' } = navigator;
    if (isbot(userAgent)) {
        return {
            isBot: true,
            type: 'crawler',
            name: getBotName(userAgent),
            category: null,
            sources: ['user_agent'],
        };
    }
    if (navigator.webdriver === true) {
        return {
            isBot: true,
            type: 'automated',
            name: null,
            category: null,
            sources: ['webdriver'],
        };
    }
    return noBot;
}

export function mergeBots(...bots: (Bot | null | undefined)[]): Bot {
    const detected = bots.filter((bot): bot is Bot => !!bot && bot.isBot);
    if (detected.length === 0) {
        return noBot;
    }
    const type = types.find((it) => detected.some((bot) => bot.type === it)) || 'crawler';
    return {
        isBot: true,
        type,
        name: detected.find((bot) => bot.name !== null)?.name || null,
        category: detected.find((bot) => bot.category !== null)?.category || null,
        sources: [...new Set(detected.flatMap((bot) => bot.sources))],
    };
}
